import { MentorShort } from '../../../../api/types/mentorShort';
import { AccessSegment, HomeShortsFeed, ShortNote, UserLevel } from '../../../../api/homeShortsFeeds/types';

export interface ShortRow extends MentorShort {
  authorName: string;
}

export const DEFAULT_FEED_ID = 'default';
export const NEW_SHORT_DAYS = 14;
export const EXAMPLE_CSV_FILENAME = 'homepage-shorts-example.csv';
export const CSV_HEADER = ['position', 'short_id', 'mentor', 'title', 'worth', 'why', 'gold_star'];

export const ACCESS_LABELS: Record<AccessSegment, string> = {
  free: 'Free',
  trial: 'Trial',
  subscriber: 'Subscriber',
};

export const createFeedId = () => `feed-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const formatDuration = (seconds: number) => {
  const total = Number(seconds) || 0;
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export const isNewShort = (short: MentorShort) =>
  !!short.createdAt && Date.now() - short.createdAt < NEW_SHORT_DAYS * 24 * 60 * 60 * 1000;

export const levelLabel = (level: UserLevel) => level.title;

// [1, 2, 3, 5] -> "1–3, 5"
const summarizeLevels = (levels: number[]) => {
  const sorted = [...levels].sort((a, b) => a - b);
  const parts: string[] = [];
  let start = 0;
  for (let i = 1; i <= sorted.length; i++) {
    if (i === sorted.length || sorted[i] !== sorted[i - 1] + 1) {
      parts.push(i - start > 1 ? `${sorted[start]}–${sorted[i - 1]}` : `${sorted[start]}`);
      start = i;
    }
  }
  return parts.join(', ');
};

// "Levels 1–3 · Free, Trial" or "Any level · Subscriber"
export const targetingSummary = (feed: Pick<HomeShortsFeed, 'levels' | 'access'>) => {
  const levelText = feed.levels.length
    ? `${feed.levels.length > 1 ? 'Levels' : 'Level'} ${summarizeLevels(feed.levels)}`
    : 'Any level';
  const accessText = feed.access.length ? feed.access.map((item) => ACCESS_LABELS[item]).join(', ') : 'Any access';
  return `${levelText} · ${accessText}`;
};

// Moves one id to a new index, clamped to the list.
export const moveId = (ids: string[], id: string, toIndex: number) => {
  const from = ids.indexOf(id);
  if (from < 0) {
    return ids;
  }
  const next = [...ids];
  next.splice(from, 1);
  next.splice(Math.min(Math.max(toIndex, 0), next.length), 0, id);
  return next;
};

// RFC 4180 parser: quoted fields, "" escapes, CRLF/LF/CR row breaks, newlines inside quotes.
export const parseCsv = (input: string): string[][] => {
  const text = input.replace(/^﻿/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') {
        i++;
      }
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop fully blank lines.
  return rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
};

const escapeCsvField = (value: string) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

export const serializeCsv = (rows: string[][]) => rows.map((cells) => cells.map(escapeCsvField).join(',')).join('\r\n');

export const shortsToCsvRows = (
  ids: string[],
  shortsById: Map<string, ShortRow>,
  notes: Record<string, ShortNote> = {},
) =>
  ids.map((id, index) => [
    String(index + 1),
    id,
    shortsById.get(id)?.authorName ?? '',
    shortsById.get(id)?.title ?? '',
    notes[id]?.worth != null ? String(notes[id].worth) : '',
    notes[id]?.why ?? '',
    notes[id]?.goldStar ? 'yes' : '',
  ]);

export const isNoteEmpty = (note?: ShortNote) => !note || (note.worth === null && !note.why.trim() && !note.goldStar);

export type WorthColor = 'success' | 'primary' | 'default';

// >= 8 success, >= 6 primary, otherwise default.
export const worthColor = (worth: number): WorthColor => (worth >= 8 ? 'success' : worth >= 6 ? 'primary' : 'default');

// Highest worth first, ties by gold star, then current order. Shorts without worth go last in current order.
export const sortIdsByWorth = (ids: string[], notes: Record<string, ShortNote>) =>
  ids
    .map((id, index) => ({ id, index, worth: notes[id]?.worth ?? null, gold: !!notes[id]?.goldStar }))
    .sort((a, b) => {
      if (a.worth === null || b.worth === null) {
        return a.worth === b.worth ? a.index - b.index : a.worth === null ? 1 : -1;
      }
      return b.worth - a.worth || Number(b.gold) - Number(a.gold) || a.index - b.index;
    })
    .map((item) => item.id);

export const buildCsv = (rows: string[][]) => serializeCsv([CSV_HEADER, ...rows]);

export const downloadCsv = (filename: string, content: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'feed';

export interface CsvImportAnalysis {
  /** valid, de-duplicated ids in the order they would be applied */
  importedIds: string[];
  unknownIds: string[];
  duplicateIds: string[];
  /** ids in the current feed that the CSV does not mention */
  missingIds: string[];
  /** notes from rows with a known short_id and any of worth/why/gold_star set */
  notes: Record<string, ShortNote>;
}

export type CsvAnalysisResult = { error: string } | { analysis: CsvImportAnalysis };

const TRUE_VALUES = ['yes', 'true', '1', 'y'];

// Reads `short_id` (required), `position`, `worth`, `why` and `gold_star` (optional). Other columns are ignored.
export const analyzeCsv = (
  text: string,
  shortsById: Map<string, ShortRow>,
  currentIds: string[],
): CsvAnalysisResult => {
  const rows = parseCsv(text);
  if (rows.length === 0) {
    return { error: 'The file is empty.' };
  }
  const header = rows[0].map((cell) => cell.trim().toLowerCase());
  const idColumn = header.indexOf('short_id');
  if (idColumn < 0) {
    return { error: 'Missing required column "short_id" in the first row.' };
  }
  const positionColumn = header.indexOf('position');
  const worthColumn = header.indexOf('worth');
  const whyColumn = header.indexOf('why');
  const goldColumn = header.indexOf('gold_star');
  const cell = (cells: string[], column: number) => (column >= 0 ? (cells[column] ?? '').trim() : '');

  const entries = rows.slice(1).map((cells, index) => {
    const position = positionColumn >= 0 ? parseFloat((cells[positionColumn] ?? '').trim()) : NaN;
    const parsedWorth = parseFloat(cell(cells, worthColumn));
    const note: ShortNote = {
      worth: Number.isNaN(parsedWorth) ? null : parsedWorth,
      why: cell(cells, whyColumn),
      goldStar: TRUE_VALUES.includes(cell(cells, goldColumn).toLowerCase()),
    };
    return { id: (cells[idColumn] ?? '').trim(), position, index, note };
  });
  // Rows with a position sort by it; rows without one keep file order after them.
  const sorted =
    positionColumn >= 0
      ? [...entries].sort((a, b) => {
          const aHas = !Number.isNaN(a.position);
          const bHas = !Number.isNaN(b.position);
          if (aHas && bHas) {
            return a.position - b.position || a.index - b.index;
          }
          return aHas === bHas ? a.index - b.index : aHas ? -1 : 1;
        })
      : entries;

  const seen = new Set<string>();
  const importedIds: string[] = [];
  const unknownIds: string[] = [];
  const duplicateIds: string[] = [];
  const notes: Record<string, ShortNote> = {};
  sorted.forEach(({ id, note }) => {
    if (!id) {
      return;
    }
    if (seen.has(id)) {
      if (!duplicateIds.includes(id)) {
        duplicateIds.push(id);
      }
      return;
    }
    seen.add(id);
    if (shortsById.has(id)) {
      importedIds.push(id);
      if (!isNoteEmpty(note)) {
        notes[id] = note;
      }
    } else {
      unknownIds.push(id);
    }
  });

  return {
    analysis: {
      importedIds,
      unknownIds,
      duplicateIds,
      missingIds: currentIds.filter((id) => !seen.has(id)),
      notes,
    },
  };
};
